import React, { useEffect, useMemo, useState } from 'react';
import { useTable, stockFeatures, flexRender } from '@tanstack/react-table';
import { Table, Pagination, Select, Checkbox } from 'components/ui';
import TableRowSkeleton from './loaders/TableRowSkeleton';
import Loading from './Loading';

const { Tr, Th, Td, THead, TBody, Sorter } = Table;
const EMPTY = [];
const DEFAULT_PAGING = { total: 0, pageIndex: 1, pageSize: 10 };
const DEFAULT_SIZES = [10, 25, 50, 100];

// Preserve the shared component's server-side pagination and legacy column API.
export default function DataTable({ columns = EMPTY, data = EMPTY, loading = false,
  selectable = false, pageSizes = DEFAULT_SIZES, pagingData = DEFAULT_PAGING,
  autoResetSelectedRows = true, skeletonAvatarColumns, skeletonAvatarProps,
  onCheckBoxChange, onIndeterminateCheckBoxChange, onPaginationChange,
  onSelectChange, onSort }) {
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState([]);
  useEffect(() => { if (autoResetSelectedRows) setRowSelection({}); }, [data, autoResetSelectedRows]);
  const normalizedColumns = useMemo(() => {
    const adapt = list => list.map(column => ({
      ...column,
      header: column.header ?? column.Header,
      ...(typeof column.accessor === 'string' ? { accessorKey: column.accessor } : {}),
      ...(typeof column.accessor === 'function' ? { accessorFn: column.accessor } : {}),
      ...(column.Cell ? { cell: context => column.Cell({ ...context, value: context.getValue() }) } : {}),
      ...(column.columns ? { columns: adapt(column.columns) } : {}),
      enableSorting: column.sortable ?? column.enableSorting ?? false,
    }));
    const adapted = adapt(columns);
    if (selectable) adapted.unshift({ id: 'selection', header: () => null, cell: () => null, enableSorting: false });
    return adapted;
  }, [columns, selectable]);
  const table = useTable({ features: stockFeatures, data, columns: normalizedColumns,
    manualPagination: true, manualSorting: true, enableRowSelection: !loading,
    state: { rowSelection, sorting }, onRowSelectionChange: setRowSelection,
    onSortingChange: updater => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(next);
      if (!loading) onSort?.({ order: next[0] ? (next[0].desc ? 'desc' : 'asc') : '', key: next[0]?.id });
    },
  });
  return <Loading loading={loading && data.length !== 0} type="cover">
    <Table><THead>{table.getHeaderGroups().map(group => <Tr key={group.id}>
      {group.headers.map(header => <Th key={header.id} colSpan={header.colSpan}>
        {header.column.id === 'selection' ? <Checkbox checked={table.getIsAllRowsSelected()} indeterminate={table.getIsSomeRowsSelected()}
          onChange={checked => { table.toggleAllRowsSelected(checked); onIndeterminateCheckBoxChange?.(checked, table.getRowModel().rows.map(row => row.original)); }} /> :
          header.isPlaceholder ? null : <div className={header.column.getCanSort() ? 'cursor-pointer' : undefined} onClick={header.column.getToggleSortingHandler()}>
            {flexRender(header.column.columnDef.header, header.getContext())}
            {header.column.getCanSort() && <Sorter sort={header.column.getIsSorted()} />}
          </div>}
      </Th>)}
    </Tr>)}</THead>
    {loading && !data.length ? <TableRowSkeleton columns={columns.length} rows={pagingData.pageSize} avatarInColumns={skeletonAvatarColumns} avatarProps={skeletonAvatarProps} /> :
      <TBody>{table.getRowModel().rows.map(row => <Tr key={row.id}>{row.getVisibleCells().map(cell => <Td key={cell.id}>
        {cell.column.id === 'selection' ? <Checkbox checked={row.getIsSelected()} onChange={checked => { row.toggleSelected(checked); onCheckBoxChange?.(checked, row.original); }} /> : flexRender(cell.column.columnDef.cell, cell.getContext())}
      </Td>)}</Tr>)}</TBody>}
    </Table>
    <div className="flex items-center justify-between mt-4">
      <Pagination pageSize={pagingData.pageSize} currentPage={pagingData.pageIndex} total={pagingData.total} onChange={page => { if (!loading) onPaginationChange?.(page); }} />
      <div style={{ minWidth: 130 }}><Select size="sm" menuPlacement="top" isSearchable={false}
        options={pageSizes.map(value => ({ value, label: `${value} / page` }))}
        value={{ value: pagingData.pageSize, label: `${pagingData.pageSize} / page` }}
        onChange={option => { if (!loading) onSelectChange?.(option.value); }} /></div>
    </div>
  </Loading>;
}
